/**
 * Copyright (c) 2021 - 2023, Kodit Doo
 * Created by kodit-marko on 01/01/23
 */

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { IStrankaForPdvDto, PdvEvidencijeClient } from '@kodit/core/data-api';
import {
  FormBuilderTypeSafe,
  FormGroupTypeSafe,
} from 'angular-typesafe-reactive-forms-helper';
import { PdvEvidencijaPojedinacnaService } from '../../pojedinacna/pdv-evidencija-pojedinacna.service';

interface IRequest {
  searchValue: string;
}

@Component({
  selector: 'kodit-stranka-for-pdv-autocomplete',
  templateUrl: './stranka-for-pdv-autocomplete.html',
  styleUrls: ['./stranka-for-pdv-autocomplete.scss'],
})
export class StrankaForPdvAutocompleteComponent implements OnInit, OnDestroy {
  /** Subs */
  private _subs: Subscription;

  /** Props */
  strankaSuggestions: IStrankaForPdvDto[];
  strankaSuggestionsStr!: string[];
  form: FormGroupTypeSafe<IRequest>;

  /** I/O */
  @Input() strankaCtrl: AbstractControl;
  @Input() formCtrlId: string;
  @Input() parentCtrl: string;
  @Input() shouldDisable: boolean = false;
  @Output() strankaSelected = new EventEmitter<IStrankaForPdvDto | undefined>();

  constructor(
    private _client: PdvEvidencijeClient,
    private _fb: FormBuilderTypeSafe,
    private _service: PdvEvidencijaPojedinacnaService
  ) {
    this._subs = new Subscription();
    this.strankaSuggestions = [];
    this.strankaCtrl = undefined!;
    this.formCtrlId = 'stranka-for-pdv-autocomplete';
    this.parentCtrl = 'body';
    this.form = this._fb.group<IRequest>({
      searchValue: new FormControl(null, {
        validators: Validators.required,
      }),
    });
  }

  ngOnInit() {
    this.form.controls.searchValue.patchValue(this.strankaCtrl.value?.naziv);
    this._subs.add(
      this._service.getDokumentBrojChangedObservable.subscribe((res) => {
        if (!res) {
          this._setValidators();
          this.form.controls.searchValue.patchValue(null);
        } else {
          this.form.controls.searchValue.patchValue(res);
        }
      })
    );
    this._setValidators();

    this._subs.add(
      this._service.getFormSaveClickedObject.subscribe((res) => {
        if (res) {
          if (this.form.invalid) {
            this.form.markAllAsTouched();
          }
          this.strankaCtrl.markAllAsTouched();
        }
      })
    );
  }

  handleSearch(event: any) {
    if (this.form.value.searchValue.length < 1) {
      this.strankaSuggestions = [];
      return;
    }
    this._subs.add(
      this._client
        .getStrankaForPdvAutocomplete(this.form.value.searchValue)
        .subscribe((res) => {
          this.strankaSuggestions = res!.data!.responseList!;
          this.strankaSuggestionsStr = res.data!.responseList!.map(
            (x) => x.naziv!
          );
        })
    );
  }

  handleSelectChange(strankaStr: string) {
    const stranka = this.strankaSuggestions.find(x => x.naziv === strankaStr);
    this.strankaCtrl.patchValue(stranka);
    this._removeValidators();
    this.strankaSelected.emit(stranka);
  }

  handleClear() {
    this.strankaCtrl.reset();
    this.strankaCtrl.markAllAsTouched();
    this._setValidators();
    this.strankaSelected.emit(undefined);
  }

  private _setValidators() {
    this.strankaCtrl.get('naziv')?.setValidators(Validators.required);
    this.strankaCtrl.get('id')?.setValidators(Validators.required);
    this.strankaCtrl.get('id')?.updateValueAndValidity();
    this.strankaCtrl.get('naziv')?.updateValueAndValidity();
  }

  private _removeValidators() {
    this.strankaCtrl.get('naziv')?.clearValidators();
    this.strankaCtrl.get('id')?.clearValidators();
    this.strankaCtrl.get('id')?.updateValueAndValidity();
    this.strankaCtrl.get('naziv')?.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
